import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { $countriesStore } from '@lib/store';
import { TransferRecipientTypeEnum, type RecipientResponse } from '@shared/api';
import { DeleteRecipientModal, EditRecipientModal } from '@widgets';
import { Button, Icon, Table, TableCellType } from '@shared/widgets';
import EditIcon from '@assets/icons/edit.svg';
import TrashIcon from '@assets/icons/trash.svg';

interface RecipientsTableProps {
  isLoading?: boolean;
  error?: string | null;
  onRefresh?: () => void;
  data: RecipientResponse[];
  currentPage: number;
  totalItemCount: number;
  countItemPage: number;
  onChangePage: (pageNumber: number) => void;
  onChangeCountItemPage: (pageNumber: number) => void;
  onSelectedItemId: (data: string) => void;
}

export const RecipientsTable = (props: RecipientsTableProps) => {
  const countryStore = useUnit($countriesStore.store);

  const [selectedRecipient, setSelectedRecipient] = useState<RecipientResponse>();
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const { t } = useTranslation(['payments', 'recipient']);

  useEffect(() => {
    $countriesStore.events.getCountries();
  }, []);

  const deleteHandler = () => {
    setSelectedRecipient(undefined);
    setShowDeleteModal(false);
    props.onRefresh?.();
  };

  const editHandler = () => {
    setSelectedRecipient(undefined);
    setShowEditModal(false);
    props.onRefresh?.();
  };

  const columns = useMemo(
    () => [
      t('recipients.columns.name'),
      t('recipients.columns.typeRecipient'),
      t('recipients.columns.accountIbanNumber'),
      t('recipients.columns.country'),
      t('recipients.columns.currency'),
      '',
    ],
    [t],
  );
  return (
    <>
      <Table
        isScrollable
        isLoading={props.isLoading}
        error={props.error}
        onRefresh={props.onRefresh}
        columns={columns}
        items={props.data.map((row) => [
          {
            type: TableCellType.Text,
            value: row.type === TransferRecipientTypeEnum.Business ? row.companyName : `${row.firstName || ''} ${row.lastName || ''}`.trim() || '-',
          },
          { type: TableCellType.Text, value: t(`recipientType.${row.type.toLowerCase()}`, { ns: 'common' }) },
          { type: TableCellType.Text, value: row.iban || row.accountNumber || '-' },
          { type: TableCellType.Text, value: countryStore.data.find((country) => country.twoLetterCode === row.countryCode)?.name || '-' },
          { type: TableCellType.Text, value: row.currency },
          {
            type: TableCellType.Custom,
            value: (
              <div className='flex justify-end gap-2' onClick={(e) => e.stopPropagation()}>
                <Button
                  className='size-8'
                  color='icon'
                  aria-label={t('recipients.buttons.editRecipient')}
                  title={t('recipients.buttons.editRecipient')}
                  onClick={() => (setSelectedRecipient(row), setShowEditModal(true))}
                >
                  <Icon className='stroke-[1.1]' icon={<EditIcon />} />
                </Button>
                <Button
                  className='size-8'
                  color='icon'
                  aria-label={t('recipients.buttons.deleteRecipient')}
                  title={t('recipients.buttons.deleteRecipient')}
                  onClick={() => (setSelectedRecipient(row), setShowDeleteModal(true))}
                >
                  <Icon icon={<TrashIcon />} />
                </Button>
              </div>
            ),
            width: '72px',
          },
        ])}
        pagination={{
          currentPage: props.currentPage,
          totalItemCount: props.totalItemCount,
          countItemPage: props.countItemPage,
          onChangePage: props.onChangePage,
          onChangeCountItemPage: props.onChangeCountItemPage,
        }}
        onClick={(rowIndex) => props.onSelectedItemId(props.data[rowIndex].id)}
      />
      {selectedRecipient && (
        <>
          <EditRecipientModal
            isOpen={showEditModal}
            onClose={() => (setSelectedRecipient(undefined), setShowEditModal(false))}
            data={selectedRecipient}
            onSubmit={editHandler}
          />
          <DeleteRecipientModal
            isOpen={showDeleteModal}
            onClose={() => (setSelectedRecipient(undefined), setShowDeleteModal(false))}
            id={selectedRecipient.id}
            onSubmit={deleteHandler}
          />
        </>
      )}
    </>
  );
};
